import { Label } from '@progress/kendo-react-labels';
import { FormInput } from '../form-input';
import { TaskContentAnswer } from '../../types/game-document/entities/task-content';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Col, Row } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { Error } from '@progress/kendo-react-labels';

export interface AnswerNumberProps {
  answers?: TaskContentAnswer[];
  handleInputChange?: (
    value: string,
    type: 'question' | 'answer',
    answerResId?: string
  ) => void;
}

export function AnswerNumber({
  answers: answers,
  handleInputChange = () => {}
}: AnswerNumberProps) {
  const [requiresRange, setRequiresRange] = useState<boolean>(() => {
    const min = (answers?.[0]?.description ?? '').toString().trim();
    const max = (answers?.[1]?.description ?? '').toString().trim();
    return Boolean(min && max);
  });

  const [minError, setMinError] = useState<string>('');
  const [maxError, setMaxError] = useState<string>('');

  useEffect(() => {
    const min = (answers?.[0]?.description ?? '').toString().trim();
    const max = (answers?.[1]?.description ?? '').toString().trim();
    setRequiresRange(Boolean(min && max));

    // validate numeric values only when both filled
    if (min !== '' && max !== '') {
      const minVal = Number(min);
      const maxVal = Number(max);

      if (isNaN(minVal) || isNaN(maxVal)) {
        setMinError('Please enter valid numeric values.');
        setMaxError('Please enter valid numeric values.');
      } else if (minVal > maxVal) {
        setMinError('Minimum value cannot be greater than maximum value.');
        setMaxError('Maximum value cannot be lower than minimum value.');
      } else {
        setMinError('');
        setMaxError('');
      }
    } else {
      setMinError('');
      setMaxError('');
    }
  }, [answers]);

  const validateNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const regex = /^[0-9.]$/;
    const value = e.currentTarget.value as string;
    const navigationKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab'
    ];
    if (navigationKeys.includes(e.key)) {
      return;
    }
    if (e.key === '.' && value.includes('.')) {
      e.preventDefault();
      return;
    }
    if (!regex.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <>
      {answers && answers.length >= 2 ? (
        <>
          <Row>
            <Col md={12}>
              <Label className={'mt-2'}>Minimum correct answer</Label>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormInput
                name={'answerResId'}
                type={'number'}
                onKeyDown={validateNumberInput}
                onChange={(e) => {
                  handleInputChange(
                    e.target.value as string,
                    'answer',
                    answers![0].answerResId
                  );
                }}
                value={answers![0].description}
              />
              {minError && <Error>{minError}</Error>}
            </Col>
            <Col md={6}>
              <Checkbox
                className={'p-1 mt-1'}
                size={'large'}
                name={'requires-range'}
                label={'Requires range'}
                value={requiresRange}
                checked={requiresRange}
                disabled={true}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Label className={'mt-3'}>Maximum correct answer</Label>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormInput
                name={'answerResId'}
                type={'number'}
                onKeyDown={validateNumberInput}
                onChange={(e) => {
                  handleInputChange(
                    e.target.value as string,
                    'answer',
                    answers![1].answerResId
                  );
                }}
                value={answers![1].description}
              />
              {maxError && <Error>{maxError}</Error>}
            </Col>
          </Row>
          <Row className={'mt-2'}>
            <Col md={12}>
              <Label className={'answer-number-note'}>
                Note: If the answer is a specific number value, set both numbers
                to the same value.
              </Label>
            </Col>
          </Row>
        </>
      ) : null}
    </>
  );
}
